import type { DiscoverProgramGamePostRequest } from "libs/apiClient";
import { _ } from "i18n";
import { normalizeOptionalLinks } from "../../../links/discoverGameLinks";
import type {
	OwnershipOption,
	ProgramGameFormErrors,
	ProgramGameFormState,
	ProgramGameSelectOption,
} from "./types";
import { DEFAULT_SELECTED_OWNERSHIP } from "./constants";

export function normalizeProgramGameOption(option: unknown): ProgramGameSelectOption | null {
	if (!option || typeof option !== "object") {
		return null;
	}
	const candidate = option as { value?: unknown; label?: unknown };
	if (candidate.value === undefined || typeof candidate.label !== "string") {
		return null;
	}
	if (typeof candidate.value !== "string" && typeof candidate.value !== "number") {
		return null;
	}
	return {
		value: candidate.value,
		label: candidate.label,
	};
}

export function programGameErrorMessage(error: unknown): string {
	if (
		typeof error === "object" &&
		error !== null &&
		"json" in error &&
		typeof (error as { json?: unknown }).json === "string"
	) {
		return (error as { json: string }).json;
	}
	if (error instanceof Error && error.message) {
		return error.message;
	}
	return _("eyeball.something.wrong");
}

export function validateProgramGameForm(
	state: ProgramGameFormState,
	isAdminUser: boolean,
	// Non-partner clubs have no teams; when the selected club has none, the team field is optional.
	options?: { homeClubHasNoTeams?: boolean; awayClubHasNoTeams?: boolean },
): ProgramGameFormErrors {
	const errors: ProgramGameFormErrors = {};
	if (!state.homeClub) {
		errors.homeClub = _("discover.gamesTable.programGame.error.homeClubRequired");
	}
	if (!state.homeTeam && !options?.homeClubHasNoTeams) {
		errors.homeTeam = _("discover.gamesTable.programGame.error.homeTeamRequired");
	}
	if (!state.awayClub) {
		errors.awayClub = _("discover.gamesTable.programGame.error.awayClubRequired");
	}
	if (!state.awayTeam && !options?.awayClubHasNoTeams) {
		errors.awayTeam = _("discover.gamesTable.programGame.error.awayTeamRequired");
	}
	if (!state.gameType) {
		errors.gameType = _("discover.gamesTable.programGame.error.typeRequired");
	}
	if (!state.gameDate) {
		errors.gameDate = _("discover.gamesTable.programGame.error.dateRequired");
	}
	if (!state.kickOffHour || !state.kickOffMinute) {
		errors.kickOff = _("discover.gamesTable.programGame.error.kickOffRequired");
	}
	if (!state.isNational) {
		errors.isNational = _("discover.gamesTable.programGame.error.isNationalRequired");
	}
	// A national game requires a national league selection.
	if (state.isNational?.value === "true" && !state.nationalLeague) {
		errors.nationalLeague = _("discover.gamesTable.programGame.error.nationalLeagueRequired");
	}
	if (isAdminUser && (!state.ownershipSelections || state.ownershipSelections.length === 0)) {
		errors.ownership = _("discover.gamesTable.programGame.error.ownershipRequired");
	}
	if (!state.priority) {
		errors.priority = _("discover.gamesTable.programGame.error.priorityRequired");
	}
	return errors;
}

export function defaultOwnershipSelections(items: OwnershipOption[]): OwnershipOption[] {
	const eyeball = items.find((item) => item.label.trim().toLowerCase() === DEFAULT_SELECTED_OWNERSHIP);
	if (eyeball) {
		return [eyeball];
	}
	return [];
}

export function buildProgramGamePostBody(
	form: ProgramGameFormState,
	ownershipIdsForSubmit: number[],
): DiscoverProgramGamePostRequest {
	const teamsheetLinks = normalizeOptionalLinks(form.teamsheetLinks);
	const videoLinks = normalizeOptionalLinks(form.videoLinks);

	return {
		homeClubId: Number(form.homeClub!.value),
		// Null when the club has no teams (non-partner club) — the team field is optional then.
		homeTeamId: form.homeTeam ? Number(form.homeTeam.value) : null,
		awayClubId: Number(form.awayClub!.value),
		awayTeamId: form.awayTeam ? Number(form.awayTeam.value) : null,
		gameDate: form.gameDate,
		kickOffHour: String(form.kickOffHour!.value).padStart(2, "0"),
		kickOffMinute: String(form.kickOffMinute!.value).padStart(2, "0"),
		isNational: form.isNational!.value === "true",
		type: String(form.gameType!.value),
		gameOwnerIds: ownershipIdsForSubmit,
		priority: Number(form.priority!.value),
		teamsheetLinks: teamsheetLinks.length > 0 ? teamsheetLinks : null,
		videoLinks: videoLinks.length > 0 ? videoLinks : null,
		gender: form.gender ? (form.gender.value as DiscoverProgramGamePostRequest["gender"]) : null,
		// Required when national (see backend Expression); null otherwise.
		nationalLeagueId: form.nationalLeague ? Number(form.nationalLeague.value) : null,
	};
}
